import React, { useState } from 'react'
import JSZip from 'jszip'
import FileSaver from 'file-saver'

import Loadable from 'src/utils/Loadable'

import { getFileBlobsFromUrls } from './getFileBlobsFromUrls'
import { dataURIToBlob } from './dataURIToBlob'

/**
 * @returns {[() => Promise<void>, boolean]}
 */
const useDownloadZip = props => {
  const [zipLoading, setZipLoading] = useState(false)
  const { attachments = [], additionalDetails, pdf } = props
  const alarm = props.entity

  const additionalDetailsData = Loadable.isSuccess(additionalDetails)
    ? additionalDetails.data
    : {}

  const texts = additionalDetailsData.textDatas || []
  const imageUrls = additionalDetailsData.imageUrls || []
  const videoUrls = additionalDetailsData.videoUrls || []
  const audioUrls = additionalDetailsData.audioUrls || []

  const downloadZip = React.useCallback(async () => {
    if (zipLoading) return
    if (pdf.loading || pdf.error !== null || pdf.blob === null) return

    /** @type {Array<import('src/parser/entities/Alarm/Preview/Provider/useAttachments').TAttachmentWithURI>} */
    const attachmentsData = Loadable.isSuccess(attachments)
      ? attachments.data
      : []
    const attachmentsError = checkError(attachments)
    const additionalDetailsError = checkError(additionalDetails)

    setZipLoading(true)

    const pdfName = `report-${alarm.id}.pdf`
    const zipName = `report-${alarm.id}.zip`

    const zip = new JSZip()

    zip.file(pdfName, pdf.blob)
    if (additionalDetailsError) {
      zip.file('ERROR_additional_details.txt', additionalDetailsError)
    }
    if (attachmentsError) {
      zip.file('ERROR_attachments.txt', attachmentsError)
    }

    /** @type {Set<string>} */
    const usedNames = new Set()

    /** @argument {string} suggestedName */
    const makeUniqueName = suggestedName => {
      let name = suggestedName
      let counter = 1
      const lastDot = name.lastIndexOf('.')
      const base = lastDot === -1 ? name : name.slice(0, lastDot)
      const ext = lastDot === -1 ? '' : name.slice(lastDot)

      while (usedNames.has(name)) {
        name = `${base}-${counter}${ext}`
        counter++
      }
      usedNames.add(name)
      return name
    }

    /** @type {string[]} */
    const urlsFromAttachments = []
    attachmentsData.forEach((attachment, i) => {
      if (
        attachment.type.endsWith('Url') &&
        typeof attachment.data?.url === 'string'
      ) {
        urlsFromAttachments.push(attachment.data?.url)
        return
      }

      if (attachment.file == null || typeof attachment.file.name !== 'string')
        return

      /** @type {string} */
      const name = cleanupFilename(attachment.file.name)
      attachment.dataURI.andThen(dataURIToBlob).match(
        blob => {
          zip.file(makeUniqueName(name), blob)
        },
        error => {
          zip.file(
            makeUniqueName(`ERROR_attachment_${name}.txt`),
            error.toString(),
          )
        },
      )
    })

    Object.entries(
      await getFileBlobsFromUrls([
        ...videoUrls,
        ...audioUrls,
        ...imageUrls,
        ...urlsFromAttachments,
      ]),
    ).forEach(([url, blob], i) => {
      const name = getResourceNameFromUrl(url)

      blob.match(
        it => zip.file(makeUniqueName(name), it),
        error => {
          zip.file(makeUniqueName(`ERROR-${name}.txt`), error.toString())
        },
      )
    })

    texts.forEach((text, i) => {
      zip.file(`AttachedText_${i}.txt`, text)
    })

    const zipBlob = await zip.generateAsync({ type: 'blob' })
    await FileSaver.saveAs(zipBlob, zipName)

    setZipLoading(false)
  }, [
    attachments,
    additionalDetails,
    texts,
    imageUrls,
    videoUrls,
    audioUrls,
    zipLoading,
    alarm.id,
    pdf.blob,
    pdf.error,
    pdf.loading,
  ])

  return [downloadZip, zipLoading]
}

export default useDownloadZip

const getResourceNameFromUrl = (url = '') => {
  const index = url.lastIndexOf('/') + 1
  const name = url.substr(index)

  return cleanupFilename(name)
}

const checkError = resource => {
  const isFailure = Loadable.isFailure(resource)
  if (isFailure) {
    return resource.error.toString()
  }
  return null
}

/** remove characters that are banned from file names */
const cleanupFilename = filename => {
  return filename.replace(/[^a-z0-9.()-]/gi, '_')
}
